import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ReactGA from 'react-ga4';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, useStripe } from '@stripe/react-stripe-js';
import axios from 'config/axios';
import { useAppDispatch, useAppSelector } from 'hooks/useStore';
import { setError } from 'store/error';

const stripePromise = loadStripe(process.env.STRIPE_PUBLISHABLE_KEY);

function Stripe() {
    const stripe = useStripe();
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const { user } = useAppSelector(state => state.user);

    useEffect(() => {
        if (!stripe) return;

        const clientSecret = new URLSearchParams(location.search).get(
            'payment_intent_client_secret'
        );

        if (!clientSecret) return;

        stripe.retrievePaymentIntent(clientSecret).then(async ({ paymentIntent }) => {
            switch (paymentIntent.status) {
                case 'succeeded':
                    // Payment succeeded, register student in course and record transaction

                    const promoCode = new URLSearchParams(location.search).get('promoCode');

                    try {
                        const {
                            data: {
                                cartBundles: bundles,
                                cartTests: tests,
                                cartCourses: courses
                            }
                        } = await axios.get('payments/cart/');

                        const { data: { orderNumber} } = await axios.post('payments/transaction/', {
                            bundles: bundles.map(bundle => bundle.id),
                            courses: courses.map(course => course.urlSlug),
                            tests: tests.map(test => test.testId),
                            paymentId: paymentIntent.id,
                            paymentGateway: 'STRIPE',
                            paymentAmount: paymentIntent.amount/100,
                            discounts: [
                                ...bundles.map(bundle => bundle.discount?.name),
                                ...courses.map(course => course.discount?.name),
                                ...tests.map(test => test.discount?.name)
                            ],
                            promoCode: promoCode
                        });

                        navigate(`/payment/${orderNumber}`, { replace: true });

                        try {
                            ReactGA.event('purchase', {
                                currency: 'USD',
                                transaction_id: orderNumber,
                                value: paymentIntent.amount/100,
                                coupon: promoCode,
                                items: [
                                    ...bundles.filter(bundle => (
                                        !bundle.numRequired
                                    )).reduce((bundleItemList, bundle) => (
                                        [
                                            ...bundleItemList,
                                            {
                                                item_id: bundle.id,
                                                item_name: bundle.name,
                                                price: bundle.courses.reduce((total, course) => (
                                                        total + course.tuition
                                                    ), 0) + bundle.tests.reduce((total, test) => (
                                                        total + test.price
                                                    ), 0),
                                                discount: bundle.discount?.type === 'Percent'
                                                    ?   (bundle.courses.reduce((total, course) => (
                                                            total + course.tuition
                                                        ), 0) + bundle.tests.reduce((total, test) => (
                                                            total + test.price
                                                        ), 0)) * bundle.discount.value / 100
                                                    :   bundle.discount?.type === 'Amount'
                                                    ?   bundle.discount?.value
                                                    :   0
                                            }
                                        ]
                                    ), []),
                                    ...courses.map(course => ({
                                        item_id: course.urlSlug,
                                        item_name: course.name,
                                        price: course.tuition,
                                        discount: course.discount?.type === 'Percent'
                                            ?   course.tuition * course.discount.value / 100
                                            :   course.discount?.type === 'Amount'
                                            ?   course.discount.value
                                            :   0
                                    })),
                                    ...tests.map(test => ({
                                        item_id: test.testId,
                                        item_name: test.name,
                                        price: test.price,
                                        discount: test.discount?.type === 'Percent'
                                            ?   test.price * test.discount.value / 100
                                            :   test.discount?.type === 'Amount'
                                            ?   test.discount.value
                                            :   0
                                    }))
                                ]
                            });
                        }
                        catch (error) {
                            console.log(error);
                            axios.post('debug/', {
                                email: user.email,
                                message: JSON.stringify(error, Object.getOwnPropertyNames(error))
                            });
                        }
                    }
                    catch (error) {
                        dispatch(setError(error));
                            
                        // If there is an error registering student, issue a refund
                        // axios.post('payments/transaction/cancel/', {
                        //     paymentId: paymentIntent.id
                        // });
                    }                    
                    break;

                case 'processing':
                    console.log('Payment processing. We\'ll update you when payment is received.');
                    break;

                case 'requires_payment_method':
                    console.log('Payment failed. Please try another payment method.');
                    // Payment failed, redirect user back to the checkout page
                    navigate('/checkout');
                    break;

                default: 
                    console.log('Something went wrong.');
                    // Payment failed, redirect user back to the checkout page
                    navigate('/checkout');
                    break;
            }
        });

    }, [stripe]);

    return <></>;
}

function StripeConfirm() {
    return (
        <Elements stripe={stripePromise}>
            <Stripe />
        </Elements>
    );
}

export default StripeConfirm;